import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Phase 191 — "Read Only — view all" sessions: the server refuses every write / export; this short-circuits them in the
// browser too, so a missed button shows one friendly toast instead of a red error. AuthContext flips the flag.
let readOnlySession = false;
export const setReadOnlySession = (on) => {
  readOnlySession = !!on;
};
const READ_ONLY_OPEN = ["/auth/", "/notifications"];
const READ_ONLY_EXPORT_RE = /(\.(pdf|csv|xlsx|xls|zip|ics|docx)$)|(\/export(\/|\.|$))|(\/(pdf|csv|excel)(\/|$))|(-pdf(\/|$))/i;
let lastReadOnlyToast = 0;
const readOnlyToast = (message) => {
  const now = Date.now();
  if (now - lastReadOnlyToast < 2500) return;
  lastReadOnlyToast = now;
  import("sonner").then(({ toast }) => toast.info(message, { id: "read-only-mode" })).catch(() => {});
};
const readOnlyRefusal = (config) => {
  if (!readOnlySession) return null;
  const method = (config.method || "get").toLowerCase();
  const url = (config.url || "").split("?")[0];
  if (method !== "get" && method !== "head" && method !== "options") {
    if (READ_ONLY_OPEN.some((p) => url.startsWith(p) || url.startsWith(`/api${p}`))) return null;
    return "View-only account — you can see everything but can't make changes.";
  }
  if (READ_ONLY_EXPORT_RE.test(url)) return "View-only account — downloads and exports are turned off.";
  return null;
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const refusal = readOnlyRefusal(config);
  if (refusal) {
    readOnlyToast(refusal);
    const err = new axios.Cancel(refusal);
    err.readOnly = true;
    err.response = { status: 403, data: { detail: refusal } };
    return Promise.reject(err);
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && window.location.pathname !== "/auth") {
      localStorage.removeItem("bf_token");
      window.location.href = "/auth";
    }
    if (err.response?.status === 403 && /view only/i.test(String(err.response?.data?.detail || ""))) {
      readOnlyToast(err.response.data.detail);
    }
    return Promise.reject(err);
  }
);

export const getToken = () => localStorage.getItem("bf_token");

export const errMsg = (e, fallback = "Something went wrong") => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail) return detail;
  if (Array.isArray(detail) && detail.length) {
    // FastAPI/Pydantic validation errors come as [{type, loc, msg, input, url}]
    return detail
      .slice(0, 3)
      .map((d) => (typeof d === "string" ? d : `${(d?.loc || []).slice(1).join(".")}: ${d?.msg || "invalid"}`))
      .join(" · ");
  }
  if (detail && typeof detail === "object") return detail.msg || JSON.stringify(detail).slice(0, 200);
  return e?.message || fallback;
};
